import { addQueryParam, downloadBlob } from "@/lib/utils";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";
import { type AxiosResponse } from "axios";
import { type DateRange } from "react-day-picker";
import {
  type ApiPaginatedResponse,
  type ApiResponse,
  apiClient,
  isApiError,
  unwrapOrThrow,
} from "./_client";
import { type FeedbackStatus } from "./answer-feedback";
import { type GraphMetadata } from "./graph-metadata";
import {
  type DocumentCategory,
  type DocumentTag,
  type DocumentTypeId,
} from "./documents";

export interface AnswerGroup {
  id: string;
  group: {
    id: string;
    explanation: string;
  };
  documents: {
    id: string; // doc_id or source_project (id)
    filename: string | null;
    document_id: string | null;
    source_project: string | null;
    extracted_from_project: boolean;
  }[];
  answers: Answer[];
  is_current_value: boolean;
  selection_type: "current_value" | "user_selected" | null;
  // Org-latest value-feedback status; null = unreviewed. Drives the Q&A
  // "Rejected"/"Unsure" badge and the review-status filter. The server
  // sends null for users without org-wide feedback read access.
  latest_feedback_status: FeedbackStatus | null;
  drd: string | null;
  is_time_series?: boolean;
  time_series_label?: string;
  time_series_order?: number | null;
  time_series_period_start?: string | null;
  time_series_period_end?: string | null;
  time_series_unit?: string;
}

// Type for a single citation
export type ProjectCitation = {
  extracted_from_project: true;
  id: string;
  citation: string;
  source_project: string;
  document_id: null;
  filename: null;
  page: null;
  document_drd: null;
};

type DocumentCitationNotFound = {
  extracted_from_project: false;
  id: string;
  citation: null;
  page: null;
  document_id: string;
  filename: string;
  document_drd: string;
  source_project: null;
  bboxes?: BBox[] | null;
  bbox_grounded?: boolean;
};

export type BBox = {
  Left: number;
  Top: number;
  Width: number;
  Height: number;
};

export type DocumentCitation = {
  extracted_from_project: false;
  id: string;
  citation: string;
  document_id: string;
  filename: string;
  page: number | null;
  document_drd: string;
  source_project: null;
  bboxes?: BBox[] | null;
  // True when `bboxes` was matched to the answer's supporting line (precise,
  // trustworthy) vs a raw page anchor. Graph citations only get a highlight
  // when grounded; ungrounded ones show a page-level source reference.
  bbox_grounded?: boolean;
  // Spreadsheet cell-level citation (natively-parsed XLSX). When sheet_name
  // is set the citation points at a cell, not a page; cell_imprecise means
  // only a sheet/row-range could be verified, not a single cell.
  sheet_name?: string | null;
  cell_ref?: string | null;
  row_index?: number | null;
  col_index?: number | null;
  cell_imprecise?: boolean;
};

export type Citation =
  | DocumentCitationNotFound
  | DocumentCitation
  | ProjectCitation;

export interface Answer {
  id: string;
  text: string;
  explanation: string;
  is_composite: boolean;
  // Persisted mode that actually produced this answer (edit-proof, unlike the
  // question's answer_mode). "graph" / "graph_full" mark broad graph citations.
  source_mode?: string | null;
  citations: Citation[];
  created_at: string;
  flagged: boolean;
  excluded_at?: string | null;
  error: string | null;
  project_id: string;
  project_name?: string | null;
  portfolio_id: string | null;
  document_drilldown: string[] | null;
  in_progress?: boolean;
  is_current_value: boolean;
  // Hallucination detection metadata
  grounding_confidence: number | null;
  hallucination_flags: string[];
  // Signature detection
  signature_data?: Array<{
    signer_name: string;
    raw_signer_name: string;
    signer_role: string;
    signer_company: string;
    signed: boolean;
    confidence: number;
    page: number | null;
    method: string;
    source_document_id?: string;
  }>;
  graph_metadata?: GraphMetadata;
}

export interface AnswerCurationResult {
  answer_id: string;
  answer_group_id: string;
  excluded_at: string | null;
  // true when this was the last supporting document, so the group's value was
  // marked incorrect (equivalent to curating the whole group out).
  group_marked_incorrect?: boolean;
}

export const curateOutAnswer = async ({
  answerId,
  answerGroupId,
  reason,
}: {
  answerId: string;
  answerGroupId: string;
  reason?: string;
}): Promise<AnswerCurationResult> => {
  const response = await apiClient.post<
    { answer_group_id: string; reason?: string },
    AxiosResponse<AnswerCurationResult>
  >(`api/documents/answers/${answerId}/curate-out/`, {
    answer_group_id: answerGroupId,
    reason,
  });
  return response.data;
};

// Canonical curate-out toast copy, shared by the Q&A row and the doc viewer so
// the two surfaces don't drift apart.
export const curateOutSuccessToast = (result: AnswerCurationResult) => ({
  title: result.group_marked_incorrect
    ? "Answer removed and marked incorrect"
    : "Answer removed from group",
  description: result.group_marked_incorrect
    ? "That was the only supporting document, so the answer group's value was marked incorrect."
    : "This document's answer is no longer active in the group.",
});

export const curateOutErrorToast = (e: unknown) => ({
  title: "Couldn't remove answer",
  description: e instanceof Error ? e.message : "Try again in a moment.",
  variant: "destructive" as const,
});
export interface Question {
  id: string;
  question_id: string;
  question: string;
  extra_prompt?: string;
  output_type: (typeof OutputTypeIdEnum)[number];
  document_type: DocumentTypeId;
  question_category: QuestionCategory;
  document_tags: DocumentTag[];
  document_categories: DocumentCategory[];
  project_document_categories?: string[];
  portfolio_document_categories?: string[];
  project_id: string;
  is_global: boolean;
  use_current_value: boolean;
  answer_mode: AnswerModeId;
  variable_name: string;
  source?: QuestionSource | null;
  generated_extraction_prompt?: string;
  generated_extraction_keywords?: string;
  // Compatibility field. Question edits keep template links intact, so this
  // should normally be empty.
  detached_analysis_ids?: string[];
  analysis_edit_context?: {
    kind: "derived_report" | "template" | "standalone";
    report_title?: string | null;
    template_title?: string | null;
    derived_report_count?: number;
    template_count?: number;
    analysis_count?: number;
  } | null;
}

// Question source can be a JSON object with different structures
export type QuestionSource = {
  source_type:
    | "manual"
    | "document_context"
    | "bulk_import"
    | "duplicated"
    | "rule_generation"
    | (string & {});
  attribution?: string; // For manual
  session_id?: string; // For document_context
  org_context_id?: string; // For document_context
  documents?: Array<{ id: string; filename: string }>; // For document_context
  filename?: string; // For bulk_import
  import_type?: "question_bulk_upload" | "analysis_import"; // For bulk_import
  source_question_id?: string; // For duplicated
};

export interface QuestionCategory {
  id: string;
  name: string;
  code: string;
}

export interface TimeSeriesPoint {
  id: string;
  period_label: string | null;
  period_start: string | null;
  period_end: string | null;
  period_granularity: string | null;
  scenario: string | null;
  value: string | null;
  unit: string | null;
  vintage_date: string | null;
  source_document_id: string | null;
  source_document_name: string | null;
  source_page: number | null;
}

export interface AnsweredQuestion {
  question_id: string;
  question_text: string;
  output_type: string;
  document_type: string;
  answer_groups: AnswerGroup[];
  preferred_answer: string;
  document_categories: DocumentCategory[];
  question_category: QuestionCategory;
  project_id: string;
  use_current_value: boolean;
  // Null when no visible group is flagged as current value (always was
  // nullable server-side; the type just lied about it).
  current_value_group_id: string | null;
  answer_mode?: AnswerModeId;
  source?: QuestionSource | null;
  time_series_points?: TimeSeriesPoint[];
}

// New hook for fetching questions/answers for a specific document
export const useDocumentAnswers = ({
  documentId,
  enabled = true,
}: {
  documentId: string;
  enabled?: boolean;
}): UseQueryResult<AnsweredQuestion[]> => {
  let url = `api/documents/answers/?`;
  url = addQueryParam(url, "document_id", documentId);

  const query = useQuery<AnsweredQuestion[]>({
    enabled: !!documentId && enabled,
    queryKey: ["document-answers", documentId],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnsweredQuestion[]>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
  return query;
};

export const useAnswers = ({
  pageSize = 1000,
  pageIndex = 1,
  filter,
  ordering,
  projectId,
  portfolioId,
  questionFilter,
  filterAnswerType,
}: {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  ordering?: ColumnSort | string;
  projectId?: string;
  portfolioId?: string;
  questionFilter?: string;
  filterAnswerType?: string;
} = {}): UseQueryResult<AnsweredQuestion[]> => {
  let url = `api/documents/answers/?`;
  if (projectId) {
    url = addQueryParam(url, "project_id", projectId);
  } else if (portfolioId) {
    url = addQueryParam(url, "portfolio", portfolioId);
  }
  if (pageSize) {
    url = addQueryParam(url, "page_size", pageSize.toString());
  }
  if (pageIndex) {
    url = addQueryParam(url, "page", pageIndex.toString());
  }
  if (filter) {
    url = addQueryParam(url, "search", filter);
  }
  if (questionFilter) {
    url = addQueryParam(url, "question", questionFilter);
  }
  if (filterAnswerType) {
    url = addQueryParam(url, "answer_type", filterAnswerType);
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url = addQueryParam(url, "ordering", ordering);
    } else {
      url = addQueryParam(
        url,
        "ordering",
        `${ordering.desc ? "-" : ""}${ordering.id}`,
      );
    }
  }
  const query = useQuery<AnsweredQuestion[]>({
    enabled: !!(projectId || portfolioId),
    queryKey: [
      "answers",
      projectId,
      portfolioId,
      pageSize,
      pageIndex,
      filter,
      ordering,
      questionFilter,
      filterAnswerType,
    ],
    queryFn: async () => {
      // suppress403Redirect: the Answers tab renders its own
      // permission-denied message instead of bouncing home.
      const response = await apiClient.get<
        never,
        AxiosResponse<AnsweredQuestion[]>
      >(url, { suppress403Redirect: true });
      const data = unwrapOrThrow<AnsweredQuestion[]>(response);
      const enriched = data.map((q) => {
        const groups = q.answer_groups ?? [];
        const mappedGroups = groups.map((g) => {
          const seen = new Set<string>();
          const docs: AnswerGroup["documents"] = [];
          for (const ans of g.answers ?? []) {
            for (const cit of ans.citations ?? []) {
              const id = cit.document_id || cit.source_project;
              if (id && !seen.has(id)) {
                seen.add(id);
                docs.push({
                  id,
                  document_id: cit.document_id,
                  filename: cit.filename,
                  source_project: cit.source_project,
                  extracted_from_project: cit.extracted_from_project,
                });
              }
            }
          }
          return { ...g, documents: docs };
        });

        // Sort answer_groups so that those with is_current_value = true come first
        const sortedGroups = mappedGroups.sort((a, b) => {
          const aCurrent = a.is_current_value ? 1 : 0;
          const bCurrent = b.is_current_value ? 1 : 0;
          return bCurrent - aCurrent;
        });

        return {
          ...q,
          answer_groups: sortedGroups,
        };
      });
      return enriched;
    },
  });
  return query;
};

export const startAnswersJob = async ({
  projectId,
  portfolioId,
  force,
}: {
  projectId?: string;
  portfolioId?: string;
  force?: boolean;
}) => {
  const url = `api/documents/answers/trigger-questions/`;
  let data = {};
  if (projectId) {
    data = { project_id: projectId };
  } else if (portfolioId) {
    data = { portfolio_id: portfolioId };
  }
  if (force) {
    data = { ...data, force: true };
  }
  await apiClient.post(url, data);
};

export const startSingleAnswerJob = async ({
  projectId,
  questionId,
}: {
  projectId: string;
  questionId: string;
}) => {
  const url = `api/documents/answers/trigger-single-question/`;
  await apiClient.post(url, {
    project_id: projectId,
    question_id: questionId,
  });
};

export const deleteQuestionAnswers = async ({
  projectId,
  questionId,
}: {
  projectId: string;
  questionId: string;
}) => {
  const url = `api/documents/answers/delete-question-answers/`;
  await apiClient.post(url, {
    project_id: projectId,
    question_id: questionId,
  });
};

export const deleteProjectAnswers = async ({
  projectId,
}: {
  projectId: string;
}) => {
  const url = `api/documents/answers/delete-project-answers/`;
  await apiClient.post(url, {
    project_id: projectId,
  });
};

export const exportAnswers = async ({
  projectId,
  // portfolioId,
}: {
  projectId?: string;
  // portfolioId?: string;
}): Promise<Blob> => {
  const url = `api/documents/answers/export/?project_id=${projectId}`;
  const response = await apiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const OutputType = [
  { id: "string", name: "text" },
  { id: "number", name: "number" },
  { id: "boolean", name: "boolean" },
  { id: "date", name: "date" },
  { id: "percentage", name: "percentage" },
  { id: "list", name: "list" },
] as const;

export type OutputTypeId = (typeof OutputType)[number]["id"];
// hack for zod
export const OutputTypeIdEnum = [
  OutputType[0].id,
  ...OutputType.map((output_type) => output_type.id),
] as const;

export const AnswerMode = [
  {
    id: "standard",
    name: "Standard",
    description:
      "Uses smart retrieval to quickly find answers across all documents. Fast and cost-effective for most questions.",
  },
  {
    id: "signature",
    name: "Signature",
    description:
      "Detects and extracts signature data from stored document metadata.",
  },
  {
    id: "graph",
    name: "Graph",
    description:
      "Knowledge-graph retrieval with structured extraction. Concise answer + verbatim citation, like Standard mode but with graph-aware retrieval.",
  },
  {
    id: "graph_full",
    name: "Graph Full",
    description:
      "Long-form narrative answer synthesized from the knowledge graph. Best for browsing themes across many documents. Renders behind a View Answer dialog.",
  },
  {
    id: "data_finder",
    name: "Data Finder",
    description:
      "Finds a specific datapoint (IDs, addresses, values) by expanding your variable into a targeted search prompt. Best when the answer lives in exhibits, schedules, or appendices.",
  },
] as const;

export type AnswerModeId = (typeof AnswerMode)[number]["id"];
// Zod z.enum() requires a non-spread first argument
export const AnswerModeIdEnum = [
  AnswerMode[0].id,
  ...AnswerMode.slice(1).map((mode) => mode.id),
] as const;

interface CreateQuestionData {
  question: string;
  extra_prompt?: string;
  question_category: string;
  output_type: (typeof OutputType)[number]["id"];
  document_type: DocumentTypeId;
  document_categories?: string[]; // UUIDs
  document_tags?: string[]; // UUIDs
  project_document_categories?: string[]; // UUIDs
  portfolio_document_categories?: string[]; // UUIDs
  answer_mode?: AnswerModeId;
  variable_name?: string;
  source?: string;
  generated_extraction_prompt?: string;
  generated_extraction_keywords?: string;
}

export const createQuestion = async (
  data: CreateQuestionData,
): Promise<ApiResponse<Question>> => {
  const response = await apiClient.post<Question>(
    "api/documents/questions/",
    data,
  );
  return response;
};

export const getOneQuestion = async (id: string) => {
  const response = await apiClient.get<Question>(
    `api/documents/questions/${id}/`,
  );
  return response.data;
};

// Query key factory for questions
// Usage:
//   questionKeys.all - invalidate everything
//   questionKeys.lists() - invalidate all lists
//   questionKeys.list({ filter, ordering, dateRange }) - specific list
//   questionKeys.details() - invalidate all details
//   questionKeys.detail(id) - specific detail
export const questionKeys = {
  all: ["questions"] as const,
  lists: () => [...questionKeys.all, "list"] as const,
  list: (filters: {
    filter?: string;
    ordering?: ColumnSort | string;
    dateRange?: DateRange;
  }) =>
    [
      ...questionKeys.lists(),
      filters.filter,
      filters.ordering,
      filters.dateRange?.from,
      filters.dateRange?.to,
    ] as const,
  details: () => [...questionKeys.all, "detail"] as const,
  detail: (id: string) => [...questionKeys.details(), id] as const,
};

export const useQuestion = (
  id: string | undefined,
): UseQueryResult<Question> => {
  const enabled = Boolean(id);
  return useQuery<Question>({
    queryKey: questionKeys.detail(id ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<Question>>(
        `api/documents/questions/${id}/`,
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    enabled,
  });
};

export const useQuestions = (
  filter?: string,
  ordering?: ColumnSort | string,
  dateRangeFilter: DateRange | undefined = undefined,
): UseQueryResult<Question[]> => {
  const params = new URLSearchParams();
  if (filter) {
    params.set("search", filter);
  }
  if (ordering) {
    if (typeof ordering === "string") {
      params.set("ordering", ordering);
    } else {
      params.set("ordering", `${ordering.desc ? "-" : ""}${ordering.id}`);
    }
  }
  const qs = params.toString();
  let url = `api/documents/questions/${qs ? `?${qs}` : ""}`;
  return useQuery<Question[]>({
    queryKey: questionKeys.list({
      filter,
      ordering,
      dateRange: dateRangeFilter,
    }),
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<Question[]>>(
        url,
      );
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const deleteQuestion = async (id: string) => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/questions/${id}/`,
  );
  if (isApiError(response)) {
    throw response.error;
  }
  return response.data;
};

export const bulkDeleteQuestions = async (
  ids: string[],
): Promise<{ deleted_count: number; errors: Array<{ id: string; error: string }> }> => {
  const response = await apiClient.post("api/documents/questions/bulk-delete/", { ids });
  return response.data;
};

interface PatchQuestionData extends CreateQuestionData {
  id: string;
}

export const patchQuestion = async (data: PatchQuestionData) => {
  return await apiClient.patch<Question>(
    `api/documents/questions/${data.id}/`,
    data,
  );
};

type BulkQuestionCreateResponse = ApiResponse<
  {
    created_questions: Question[];
    updated_questions?: Question[];
  },
  { bulkfile: string }
>;

export const bulkUploadQuestions = async ({
  file,
}: {
  file: File;
}): Promise<BulkQuestionCreateResponse> => {
  const form = new FormData();
  form.append("bulkfile", file);
  const options = {
    method: "POST",
    url: "api/documents/questions/bulk/",
    headers: { "Content-Type": "multipart/form-data" },
    data: form,
  };
  const response = await apiClient.request<never, BulkQuestionCreateResponse>(
    options,
  );
  return response;
};

export const useQuestionCategories = (): UseQueryResult<
  ApiPaginatedResponse<QuestionCategory> | null
> => {
  return useQuery<ApiPaginatedResponse<QuestionCategory> | null>({
    queryKey: ["question-categories"],
    queryFn: async () => {
      const response = await apiClient.get(
        "api/documents/questions/categories/",
      );
      // interceptor error envelope has no `data`; React Query forbids undefined
      return response.data ?? null;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const addQuestionCategory = async ({
  name,
  code,
}: {
  name: string;
  code: string;
}) => {
  const response = await apiClient.post("api/documents/questions/categories/", {
    name,
    code,
  });
  return response;
};

export const deleteQuestionCategory = async (id: string) => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/questions/categories/${id}/`,
  );
  return response;
};

export const exportQuestionsXlsx = async (ids?: string[]) => {
  let url = `api/documents/questions/export/`;
  if (ids && ids.length > 0) {
    url += `?ids=${ids.join(",")}`;
  }
  const response = await apiClient.get(url, {
    responseType: "blob",
  });

  if (response.data) {
    downloadBlob(
      response.data,
      "questions_export.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  }

  return response;
};
